import React, { useEffect, useState } from 'react';
import { Instagram } from 'lucide-react';
import { InstagramPost } from '../types';

const InstagramFeed: React.FC = () => {
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const { collection, doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../utils/firebase');

        const feedDoc = await getDoc(doc(db, 'instagram', 'feed'));

        if (feedDoc.exists()) {
          const data = feedDoc.data();
          setPosts(data.posts || []);
        }
      } catch (error) {
        console.error('Error fetching Instagram posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Show placeholders while loading
  if (loading) {
    return (
      <section className="py-12">
        <div className="text-center mb-10">
          <a
            href="https://instagram.com/4hvnsk8"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 font-heading font-bold text-xl tracking-widest hover:text-brand-gold transition-colors"
          >
            <Instagram size={24} /> @4HVNSK8
          </a>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-12 lg:grid-cols-12">
          {[...Array(6)].map((_, idx) => (
            <div
              key={idx}
              className="relative aspect-square overflow-hidden bg-gray-200 animate-pulse md:col-span-4 lg:col-span-2"
            />
          ))}
        </div>
      </section>
    );
  }

  // Show message if no posts available
  if (posts.length === 0) {
    return (
      <section className="py-12">
        <div className="text-center mb-10">
          <a
            href="https://instagram.com/4hvnsk8"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 font-heading font-bold text-xl tracking-widest hover:text-brand-gold transition-colors"
          >
            <Instagram size={24} /> @4HVNSK8
          </a>
        </div>
        <div className="text-center text-gray-500 py-12">
          <p className="font-body">Follow us on Instagram to see our latest posts</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12">
      <div className="text-center mb-10">
        <a
          href="https://instagram.com/4hvnsk8"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 font-heading font-bold text-xl tracking-widest hover:text-brand-gold transition-colors"
        >
          <Instagram size={24} /> @4HVNSK8
        </a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-12 lg:grid-cols-12">
        {posts.map((post) => (
          <a
            key={post.id}
            href={post.permalink}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative aspect-square overflow-hidden cursor-pointer md:col-span-4 lg:col-span-2"
          >
            <img
              src={post.media_type === 'VIDEO' && post.thumbnail_url ? post.thumbnail_url : post.media_url}
              alt={post.caption || 'Instagram post'}
              className="w-full h-full object-cover filter grayscale group-hover:grayscale-0 transition-all duration-500"
            />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center text-white">
              <Instagram size={32} />
            </div>
            {post.media_type === 'VIDEO' && (
              <div className="absolute top-2 right-2 bg-black/60 rounded-full p-1.5">
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                </svg>
              </div>
            )}
          </a>
        ))}
      </div>
    </section>
  );
};

export default InstagramFeed;